import { Outlet, createFileRoute, linkOptions } from '@tanstack/react-router'
import { useState } from 'react'
import type {
  PolicyFilters,
  PolicyResponse,
  PolicySortingState,
  PolicyStatus,
} from 'types'

import StreamingExportDialog from '@/components/export/streaming-export-dialog'
import { ExportType } from '@/components/export/streaming-export-dialog.types'
import PolicyStatusTabs from '@/components/filters/policy-status-tabs'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import { PoliciesColumn } from '@/components/tables/policies/policies-columns.enum'
import PoliciesTable from '@/components/tables/policies/policies-table'
import { ViewTransitionType } from '@/css/view-transition.types'
import { DEFAULT_PAGE_SIZE } from '@/lib/table'

export const Route = createFileRoute(
  '/app/data/companies/$uuid/(mainPage)/insured/policies',
)({
  component: RouteComponent,
})

const excludedColumns: PoliciesColumn[] = [PoliciesColumn.Company]

const rowLink = (policy: PolicyResponse) =>
  linkOptions({
    from: Route.fullPath,
    to: '/app/data/companies/$uuid/insured/policies/$policyUuid',
    params: { policyUuid: policy.uuid },
    viewTransition: { types: [ViewTransitionType.SheetParentToChild] },
  })

function RouteComponent() {
  const { uuid: insuredCompanyUuid } = Route.useParams()
  const [filters, setFilters] = useState<PolicyFilters>({
    insuredCompanyUuid,
  })
  const [pagination, setPagination] = useState<CursorPaginationState>({
    cursor: undefined,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [sorting, setSorting] = useState<PolicySortingState>([])

  const handleFiltersChange = (
    updater: (filters: PolicyFilters) => PolicyFilters,
  ) => {
    setFilters((previous) => ({ ...updater(previous), insuredCompanyUuid }))
    // reset pagination to the first page on filter change
    setPagination((previous) => ({ ...previous, cursor: undefined }))
  }

  const handleSearchChange = (search: string) =>
    handleFiltersChange((previous) => ({ ...previous, search }))

  const handleStatusChange = (status: PolicyStatus | undefined) =>
    handleFiltersChange((previous) => ({ ...previous, status }))

  return (
    <div className="flex h-full w-full flex-col justify-between gap-4">
      <div className="flex items-center justify-between gap-2">
        <SearchBar
          tabIndex={1}
          text={filters.search}
          setText={handleSearchChange}
        />
        <StreamingExportDialog
          type={ExportType.Policies}
          filters={filters}
          buttonVariant="outlineGray"
        />
      </div>
      <PolicyStatusTabs
        value={filters.status}
        onValueChange={handleStatusChange}
        filters={filters}
      />
      <PoliciesTable
        filters={filters}
        setFilters={handleFiltersChange}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        rowLink={rowLink}
        excludedColumns={excludedColumns}
      />
      <Outlet />
    </div>
  )
}
